import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Video,
  Mic,
  Monitor,
  MessageSquare,
  Users,
  Lock,
  Camera,
  FileText,
  Hand,
  Layers,
  Cloud,
  Globe,
  Calendar,
  BarChart3,
  QrCode,
  Smile,
  PenTool,
  UserCheck,
  Shield,
  Settings,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const coreFeatureImages: Record<string, string> = {
  "HD video": "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=900&q=80",
  "Noise-suppressed audio": "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=900&q=80",
  "Screen share": "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=900&q=80",
  "In-meeting chat": "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80",
  "Breakout rooms": "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=80",
  "Waiting room": "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80",
};

const coreFeatureDetails: Record<string, string[]> = {
  "HD video": ["Adaptive quality for weak networks", "Balanced layout for teams and guests"],
  "Noise-suppressed audio": ["Voice-first processing", "Less typing, fan, and room noise"],
  "Screen share": ["Share tabs, windows, or full screen", "Designed for demos and reviews"],
  "In-meeting chat": ["Links, notes, and follow-ups stay visible", "Keeps side questions out of the audio"],
  "Breakout rooms": ["Split groups quickly", "Bring everyone back when discussion ends"],
  "Waiting room": ["Approve guests before entry", "Reduce unwanted meeting access"],
};

const productivityHighlights = [
  { label: "Auto-captured", value: "Notes" },
  { label: "Reusable", value: "Recordings" },
  { label: "Live", value: "Captions" },
];

const productivityDetails: Record<string, string> = {
  "Virtual backgrounds": "Keep every call client-ready with blur, replacement scenes, and polished presets.",
  "Live captions": "Make discussions easier to follow in real time, especially across accents and busy rooms.",
  "Cloud recording": "Capture the full conversation and share it after the meeting without manual exports.",
  "Webinar mode": "Run one-to-many sessions with presenter control, structured audience flow, and fewer interruptions.",
  Polls: "Collect quick feedback, decisions, and temperature checks while everyone is still in the room.",
  Transcription: "Turn long meetings into searchable text so decisions, names, and action items are easy to find.",
};

const workflowDetails: Record<string, { step: string; detail: string }> = {
  "Calendar invites": {
    step: "Schedule",
    detail: "Send one reliable meeting link through the calendars your team already uses.",
  },
  "Meeting analytics": {
    step: "Measure",
    detail: "Track attendance, duration, and engagement so recurring meetings keep improving.",
  },
  "QR-code joining": {
    step: "Join",
    detail: "Let in-room attendees move from phone to meeting with a quick scan.",
  },
  Reactions: {
    step: "Respond",
    detail: "Capture quick agreement, applause, or feedback without interrupting the speaker.",
  },
  "Screen annotation": {
    step: "Clarify",
    detail: "Mark shared screens live so reviews, walkthroughs, and decisions move faster.",
  },
  "Guest access": {
    step: "Invite",
    detail: "Bring clients and partners into the meeting without forcing account creation.",
  },
};

const hostControlDetails: Record<string, string[]> = {
  "Admin panel": ["Role policies", "Member oversight", "Meeting defaults"],
  "Participant management": ["Mute and remove", "Promote speakers", "Keep rooms focused"],
  "Raise hand & queue": ["Ordered speaker list", "Less interruption", "Clear host prompts"],
  "Meeting controls": ["Lock rooms", "Require passwords", "Moderate access"],
};

const groups = [
  {
    name: "Core",
    blurb: "The essentials, polished.",
    items: [
      { icon: Video, title: "HD video", benefit: "Crystal-clear faces.", desc: "Adaptive HD that holds up on real-world Wi-Fi." },
      { icon: Mic, title: "Noise-suppressed audio", benefit: "Sound studio-clean.", desc: "Background noise stays out, voices stay in." },
      { icon: Monitor, title: "Screen share", benefit: "Show, don't tell.", desc: "Share a tab, window, or full screen with one click." },
      { icon: MessageSquare, title: "In-meeting chat", benefit: "Side-channel comms.", desc: "Drop links, ask questions, share notes - all in-call." },
      { icon: Users, title: "Breakout rooms", benefit: "Small-group focus.", desc: "Split the call into rooms in seconds. Pull people back when ready." },
      { icon: Lock, title: "Waiting room", benefit: "You decide who joins.", desc: "Approve guests one-by-one or admit everyone at once." },
    ],
  },
  {
    name: "Productivity",
    blurb: "Less ceremony, more work.",
    items: [
      { icon: Camera, title: "Virtual backgrounds", benefit: "Look professional anywhere.", desc: "Blur, replace, or pick a clean preset." },
      { icon: FileText, title: "Live captions", benefit: "Read every word.", desc: "Real-time transcription with multi-language support." },
      { icon: Cloud, title: "Cloud recording", benefit: "Replay & share.", desc: "Save meetings to the cloud and share via secure link." },
      { icon: Hand, title: "Webinar mode", benefit: "Run focused events.", desc: "Mute attendees, control who speaks, manage Q&A." },
      { icon: Layers, title: "Polls", benefit: "Decide together, fast.", desc: "Single or multi-select polls with instant results." },
      { icon: Globe, title: "Transcription", benefit: "Searchable history.", desc: "Find any moment of any meeting by keyword." },
    ],
  },
  {
    name: "Workflow",
    blurb: "Plug into how you already work.",
    items: [
      { icon: Calendar, title: "Calendar invites", benefit: "One link, every calendar.", desc: "Send invites that just work in Google, Outlook, and Apple." },
      { icon: BarChart3, title: "Meeting analytics", benefit: "Know what worked.", desc: "Attendance, duration, engagement - all in one dashboard." },
      { icon: QrCode, title: "QR-code joining", benefit: "Frictionless mobile join.", desc: "In-room? Scan to hop in instantly." },
      { icon: Smile, title: "Reactions", benefit: "Quick feedback, no interrupt.", desc: "Thumbs up, applause, or emoji to stay in flow." },
      { icon: PenTool, title: "Screen annotation", benefit: "Point with precision.", desc: "Draw on the shared screen so everyone follows along." },
      { icon: UserCheck, title: "Guest access", benefit: "No accounts required.", desc: "Anyone can join from a link - no signup needed." },
    ],
  },
  {
    name: "Host controls",
    blurb: "Run a tight ship.",
    items: [
      { icon: Settings, title: "Admin panel", benefit: "Full control.", desc: "Manage members, roles, and meeting policies." },
      { icon: Users, title: "Participant management", benefit: "Mute, remove, promote.", desc: "Run the room - no second-guessing." },
      { icon: Hand, title: "Raise hand & queue", benefit: "Order the conversation.", desc: "Hosts see who wants to speak, in order." },
      { icon: Lock, title: "Meeting controls", benefit: "Lock, password, or both.", desc: "Tight security toggles, available per-meeting." },
    ],
  },
];

const security = [
  "End-to-end encrypted media in transit",
  "Password-protected rooms",
  "Waiting room approvals",
  "Role-based access control (host, co-host, participant)",
  "Granular host moderation tools",
  "Audit logs for admin actions",
];

const securityStats = [
  { label: "Meeting access", value: "Controlled" },
  { label: "Media transport", value: "Encrypted" },
  { label: "Host actions", value: "Audited" },
];

const faqs = [
  {
    q: "What features do I get on the free plan?",
    a: "Every feature listed on this page is available on every plan. Free users get up to 10 meetings per month, 100 participants per meeting, and 1-hour recordings stored for 2 days.",
  },
  {
    q: "Do I need to install anything?",
    a: "No. MeetHub runs entirely in modern browsers (Chrome, Edge, Firefox, Safari) on desktop and mobile.",
  },
  {
    q: "Can I record meetings?",
    a: "Yes. Recordings are saved to your account, transcribed automatically, and shareable via secure link.",
  },
];

export default function Features() {
  const navigate = useNavigate();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="Features — Professional online meetings, fully loaded · MeetHub"
        description="Everything you need to run great online meetings: HD video, screen share, breakout rooms, live captions, recording, webinar mode, and host controls. All included."
        canonicalPath="/features"
        jsonLd={jsonLd}
      />
      <Navigation />

      <section className="relative overflow-hidden bg-gradient-soft pt-24 pb-10 sm:pt-24 sm:pb-12 border-b">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="container relative max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Features</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                Full meeting stack, built for serious teams.
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-xl">
                From HD calls to host controls and workflows, MeetHub combines the features teams actually use into one clean, browser-first platform.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["HD video and audio", "Breakout rooms + polls", "Live captions + recording", "Security by default"].map((point) => (
                <div key={point} className="rounded-xl border bg-card px-4 py-3 text-sm font-medium">{point}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {groups.map((group, idx) => (
        <section key={group.name} className={`py-12 sm:py-14 ${idx % 2 ? "bg-secondary/30 border-y" : ""}`}>
          <div className="container">
            <div className="max-w-2xl mb-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">{group.name}</p>
              <h2 className="text-2xl sm:text-3xl font-bold mt-1">{group.blurb}</h2>
            </div>
            {group.name === "Core" ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.items.map((it) => (
                  <Card key={it.title} className="group overflow-hidden border-border/70 hover:border-primary/30 hover:shadow-lg transition-all">
                    <div className="relative h-44 overflow-hidden bg-secondary">
                      <img
                        src={coreFeatureImages[it.title]}
                        alt={`${it.title} feature preview`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                      <div className="absolute left-4 bottom-4 h-11 w-11 rounded-lg bg-background/90 text-primary shadow-sm inline-flex items-center justify-center backdrop-blur">
                        <it.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg">{it.title}</h3>
                      <p className="text-sm font-medium text-primary mt-1">{it.benefit}</p>
                      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{it.desc}</p>
                      <ul className="mt-5 space-y-2">
                        {coreFeatureDetails[it.title].map((detail) => (
                          <li key={detail} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : group.name === "Productivity" ? (
              <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch">
                <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[520px]">
                  <img
                    src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80"
                    alt="Team using meeting productivity tools"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/35 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                    <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                      Built for follow-through
                    </Badge>
                    <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                      Capture the work while the meeting is still happening.
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-primary-foreground/85 max-w-md">
                      Captions, recordings, polls, and transcripts reduce manual follow-up so teams leave with clear context.
                    </p>
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      {productivityHighlights.map((stat) => (
                        <div key={stat.label} className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur">
                          <p className="text-lg font-semibold">{stat.value}</p>
                          <p className="text-xs text-primary-foreground/75">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4">
                  {group.items.map((it, itemIndex) => (
                    <Card
                      key={it.title}
                      className={`border-border/70 transition-all hover:border-primary/30 hover:shadow-md ${
                        itemIndex === 0 ? "bg-gradient-soft" : "bg-card"
                      }`}
                    >
                      <CardContent className="p-5 sm:p-6">
                        <div className="flex gap-4">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center flex-shrink-0">
                            <it.icon className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                              <h3 className="font-semibold text-lg">{it.title}</h3>
                              <span className="text-xs font-medium text-primary">{it.benefit}</span>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                              {productivityDetails[it.title]}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : group.name === "Workflow" ? (
              <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
                <div className="rounded-2xl border bg-card p-5 sm:p-6 shadow-sm">
                  <div className="space-y-4">
                    {group.items.map((it, itemIndex) => {
                      const detail = workflowDetails[it.title];
                      return (
                        <div key={it.title} className="relative flex gap-4 rounded-xl border bg-background p-4 sm:p-5 hover:border-primary/30 hover:shadow-sm transition-all">
                          {itemIndex < group.items.length - 1 && (
                            <span className="absolute left-9 top-16 h-8 w-px bg-border" aria-hidden />
                          )}
                          <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center flex-shrink-0 shadow-sm">
                            <span className="text-sm font-semibold">{itemIndex + 1}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                              <p className="text-xs font-semibold uppercase tracking-widest text-primary">{detail.step}</p>
                              <h3 className="font-semibold text-lg">{it.title}</h3>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{detail.detail}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="sticky top-28 rounded-2xl border bg-gradient-soft p-6 sm:p-8 shadow-sm">
                  <Badge variant="outline" className="mb-4">Workflow fit</Badge>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                    MeetHub sits inside the way your team already works.
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    Move from invite to discussion, feedback, annotation, and analytics without adding extra process for hosts or guests.
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {["Calendar-ready", "Guest friendly", "Mobile join", "Actionable data"].map((item) => (
                      <div key={item} className="rounded-xl border bg-card/80 px-4 py-3 text-sm font-medium shadow-xs">
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 overflow-hidden rounded-xl border bg-card">
                    <img
                      src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1000&q=80"
                      alt="Team workflow planning session"
                      className="h-52 w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ) : group.name === "Host controls" ? (
              <div className="rounded-2xl border bg-foreground text-background overflow-hidden shadow-lg">
                <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
                  <div className="p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-background/15">
                    <Badge className="mb-5 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                      Host command center
                    </Badge>
                    <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                      Keep the room calm, secure, and moving.
                    </h3>
                    <p className="mt-4 text-sm sm:text-base text-background/75 leading-relaxed">
                      Hosts get the controls they need before, during, and after the meeting without digging through settings.
                    </p>

                    <div className="mt-8 space-y-3">
                      {["Host", "Co-host", "Participant"].map((role, roleIndex) => (
                        <div key={role} className="flex items-center justify-between rounded-xl border border-background/15 bg-background/5 px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="h-2.5 w-2.5 rounded-full bg-primary-glow" />
                            <span className="text-sm font-medium">{role}</span>
                          </div>
                          <span className="text-xs text-background/60">
                            {roleIndex === 0 ? "Full access" : roleIndex === 1 ? "Moderation" : "Limited"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background text-foreground p-5 sm:p-6 lg:p-8">
                    <div className="grid sm:grid-cols-2 gap-4">
                      {group.items.map((it) => (
                        <Card key={it.title} className="border-border/70 bg-card hover:border-primary/30 hover:shadow-md transition-all">
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center">
                                <it.icon className="h-5 w-5" />
                              </div>
                              <Badge variant="secondary" className="text-[11px]">{it.benefit}</Badge>
                            </div>
                            <h3 className="mt-5 font-semibold text-lg">{it.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.desc}</p>
                            <div className="mt-5 flex flex-wrap gap-2">
                              {hostControlDetails[it.title].map((detail) => (
                                <span key={detail} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                                  {detail}
                                </span>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {group.items.map((it) => (
                  <Card key={it.title} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center mb-4">
                        <it.icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-semibold text-base">{it.title}</h3>
                      <p className="text-sm font-medium text-primary mt-0.5">{it.benefit}</p>
                      <p className="text-sm text-muted-foreground mt-2">{it.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Security */}
      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="relative overflow-hidden rounded-2xl border bg-gradient-soft shadow-sm">
            <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
            <div className="relative grid lg:grid-cols-[0.9fr_1.1fr] gap-8 p-6 sm:p-8 lg:p-10">
              <div>
                <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground inline-flex items-center justify-center shadow-sm mb-5">
                  <Shield className="h-6 w-6" />
                </div>
                <Badge variant="outline" className="mb-4">Trust layer</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Security & privacy by default</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  MeetHub is built with privacy as the baseline, not an upsell. Every meeting gets practical protection for access, moderation, and sensitive collaboration.
                </p>
                <div className="mt-7 grid grid-cols-3 gap-3">
                  {securityStats.map((stat) => (
                    <div key={stat.label} className="rounded-xl border bg-card/85 p-3 shadow-xs">
                      <p className="text-sm font-semibold">{stat.value}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-5 sm:p-6 shadow-sm">
                <div className="grid sm:grid-cols-2 gap-3">
                  {security.map((s, index) => (
                    <div key={s} className="rounded-xl border bg-background p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">
                            {index < 2 ? "Access protection" : index < 4 ? "Role safeguards" : "Operational trust"}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
            <div className="rounded-2xl border bg-card p-6 sm:p-8 shadow-sm">
              <Badge variant="outline" className="mb-4">FAQ</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Common questions</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Quick answers for teams evaluating MeetHub features, access, recordings, and day-one setup.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {["No installs", "Guest access", "Recording ready", "Free to start"].map((item) => (
                  <div key={item} className="rounded-xl border bg-secondary/50 px-4 py-3 text-sm font-medium">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <Card className="shadow-sm">
              <CardContent className="p-5 sm:p-6">
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f-${i}`} className="rounded-xl border px-4">
                      <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-16">
        <div className="container max-w-6xl">
          <div className="relative overflow-hidden rounded-2xl border bg-foreground p-6 sm:p-10 lg:p-12 text-background shadow-lg">
            <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/30 to-transparent pointer-events-none" aria-hidden />
            <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <Badge className="mb-4 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                  Start in minutes
                </Badge>
                <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">Ready to run better meetings?</h2>
                <p className="mt-4 max-w-2xl text-background/75 leading-relaxed">
                  Launch browser-based meetings with captions, recording, workflow tools, and host controls. Start free, then upgrade when your team needs more scale.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 text-sm text-background/80">
                  {["No credit card", "100 participants", "Secure guest links"].map((item) => (
                    <span key={item} className="rounded-full border border-background/20 bg-background/5 px-3 py-1">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 min-w-[220px]">
                <Button size="lg" variant="secondary" className="h-12 px-7" onClick={() => navigate("/register")}>
                  Start free <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-7 bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background"
                  onClick={() => navigate("/pricing")}
                >
                  Compare plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}




